import { useState, useEffect } from 'react';
import { decodeJWT } from '../utils/jwt';
import { apiService } from '../services/api';
import { storage } from '../utils/localStorage';

interface User {
    firstName: string;
    lastName: string;
    email: string;
    tenantName?: string;
    timezone?: string;
    locale?: string;
    admin?: boolean;
    /** A full admin's session - also while they work as one of a tenant's users, when `admin` is that user's. */
    adminSession?: boolean;
}

const TENANT_CHOSEN_KEY = 'tenantChosen';

// The page an admin logged in on (a scanned QR code, a link from an e-mail) is where
// they continue after choosing the tenant. Kept in this tab only and only for as long
// as a login takes: a form opened later from a stale address could save itself.
const RETURN_PATH_KEY = 'flowy_returnPath';
const RETURN_PATH_TTL_MS = 15 * 60 * 1000;

const rememberReturnPath = (path: string) => {
    sessionStorage.setItem(RETURN_PATH_KEY, JSON.stringify({ path, at: Date.now() }));
};

const takeReturnPath = (): string | null => {
    const raw = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    if (!raw) return null;
    try {
        const { path, at } = JSON.parse(raw);
        return typeof path === 'string' && Date.now() - at <= RETURN_PATH_TTL_MS ? path : null;
    } catch {
        return null;
    }
};

const userFromToken = (token: string): User | null => {
    const decoded = decodeJWT(token);
    if (!decoded?.user) return null;
    return {
        firstName: decoded.user.firstName,
        lastName: decoded.user.lastName,
        email: decoded.user.email,
        tenantName: decoded.tenant?.company_name,
        timezone: decoded.user.timezone,
        locale: decoded.user.locale,
        admin: decoded.user.admin === true,
        adminSession: decoded.user.admin === true || !!storage.getItem('adminToken'),
    };
};

export const useAuth = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<User | null>(null);
    const [showInvalidSessionModal, setShowInvalidSessionModal] = useState(false);
    // A full admin picks the tenant to work in before the app opens; the
    // choice is remembered for the session and can be redone from the sidebar.
    const [tenantChosen, setTenantChosen] = useState(() => storage.getItem(TENANT_CHOSEN_KEY) === '1');

    useEffect(() => {
        const token = storage.getItem('token');

        setIsAuthenticated(!!token);

        if (token) {
            setUser(userFromToken(token));
        }

        setLoading(false);
    }, []);

    const handleLoginSuccess = async (token: string) => {
        const user = userFromToken(token);
        setIsAuthenticated(true);
        setUser(user);
        storage.removeItem(TENANT_CHOSEN_KEY);
        storage.removeItem('chosenTenantId');
        storage.removeItem('chosenUserId');
        setTenantChosen(false);

        // Získaj Mercure token hneď po prihlásení
        try {
            const mercureResponse = await apiService.getMercureToken();
            if (mercureResponse.success && mercureResponse.data.token) {
                storage.setItem('mercureToken', mercureResponse.data.token);
            }
        } catch (error) {
            console.error('[Auth] Failed to get Mercure token:', error);
        }

        storage.removeItem('applicationsCache');
        storage.removeItem('appearanceSchema');
        window.dispatchEvent(new Event('tokenChanged'));

        // The URL may still point into another tenant's screen (a previous
        // session); an admin picks the tenant first, so start from home and
        // come back here once the tenant is chosen.
        sessionStorage.removeItem(RETURN_PATH_KEY);
        if (user?.admin && window.location.pathname !== '/') {
            rememberReturnPath(window.location.pathname + window.location.search);
            window.location.href = '/';
        }
    };

    const [switchRequested, setSwitchRequested] = useState(false);
    const needsTenantChoice = isAuthenticated && user?.admin === true && !tenantChosen;
    const tenantModalOpen = needsTenantChoice || switchRequested;

    /** The token for the chosen tenant is already stored (apiService.selectTenant). */
    const handleTenantChosen = async (token: string) => {
        setUser(userFromToken(token));
        storage.setItem(TENANT_CHOSEN_KEY, '1');
        setTenantChosen(true);

        try {
            const mercureResponse = await apiService.getMercureToken();
            if (mercureResponse.success && mercureResponse.data.token) {
                storage.setItem('mercureToken', mercureResponse.data.token);
            }
        } catch (error) {
            console.error('[Auth] Failed to get Mercure token after tenant change:', error);
        }

        storage.removeItem('applicationsCache');
        storage.removeItem('appearanceSchema');
        window.dispatchEvent(new Event('tokenChanged'));
        window.location.href = takeReturnPath() ?? '/';
    };

    const openTenantModal = () => setSwitchRequested(true);
    const closeTenantModal = () => setSwitchRequested(false);

    const handleLogout = () => {
        storage.clear();

        setIsAuthenticated(false);
        setUser(null);
        setShowInvalidSessionModal(false);

        window.location.href = '/';
    };

    const handleRetryLogin = async () => {
        const savedEmail = storage.getItem('savedEmail');
        const savedPassword = storage.getItem('savedPassword');

        if (!savedEmail || !savedPassword) {
            return;
        }

        const response = await apiService.login({ email: savedEmail, password: savedPassword });

        if (response.success) {
            // Získaj nový Mercure token
            try {
                const mercureResponse = await apiService.getMercureToken();
                if (mercureResponse.success && mercureResponse.data.token) {
                    storage.setItem('mercureToken', mercureResponse.data.token);
                }
            } catch (error) {
                console.error('[Auth] Failed to get Mercure token on retry:', error);
            }

            setShowInvalidSessionModal(false);
            storage.removeItem('applicationsCache');
        storage.removeItem('appearanceSchema');
        window.dispatchEvent(new Event('tokenChanged'));
            window.location.reload();
        }
    };

    return {
        isAuthenticated,
        loading,
        user,
        handleLoginSuccess,
        handleLogout,
        needsTenantChoice,
        tenantModalOpen,
        openTenantModal,
        closeTenantModal,
        handleTenantChosen,
        showInvalidSessionModal,
        setShowInvalidSessionModal,
        handleRetryLogin
    };
};
